import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { LoadingButton } from '@mui/lab';
import { Alert, Card, Grid, Skeleton, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';

import { useGetIntegrationSettingsQuery, useSetupIntegrationMutation } from 'src/services/integration/integrations.service';
import { IntegrationSetting } from 'src/services/integration/integrations.types';

import { RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import { useMemo } from 'react';

// ----------------------------------------------------------------------

type IdibuSetupProps = {
  integrationId: string;
};

export default function IdibuSetupView({
  integrationId
}: IdibuSetupProps) {

  const { enqueueSnackbar } = useSnackbar();

  const { currentData: integrationSettings, isLoading: isRetrievingSettings } = useGetIntegrationSettingsQuery(
    {
      integrationId: integrationId as string,
    },
    {
      skip: !integrationId
    }
  );

  const [setupIntegration, { isLoading: isSettingUpIntegration }] = useSetupIntegrationMutation();

  const IdibuSettingsSchema = Yup.object().shape({
    webhook_secret: Yup.string().required(t('validation.required'))
  });

  const methods = useForm({
    resolver: yupResolver(IdibuSettingsSchema)
  });

  const idibuWebhookUrl = useMemo(() => `${import.meta.env.VITE_BACKEND_URL}/webhooks/idibu/${integrationId}`, [integrationId]);

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const buildSettingsDto = (data: any) => {
    if (!integrationSettings) return [];

    const settings = [];

    const webhookSecretSetting = integrationSettings.find((setting) => setting.setting_type === IntegrationSetting.WEBHOOK_SECRET);

    if (webhookSecretSetting) {
      settings.push({
        id: webhookSecretSetting.id as string,
        value: data.webhook_secret,
      });
    }

    return settings;
  }

  const handleSetupIntegration = handleSubmit(async (data: any) => {
    if (!integrationId) return;

    const settings = buildSettingsDto(data);

    try {
      await setupIntegration({
        integrationId: integrationId as string,
        settings,
        fields: []
      }).unwrap();

      enqueueSnackbar(t('integrations.detail.configuration.setup.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(t('integrations.detail.configuration.setup.errors.default'), { variant: 'error' });
    }
  });

  return (
    <Stack spacing={1} direction="column">

      <Alert variant="outlined" severity="info" sx={{ mb: 2 }}>
        {t('integrations.detail.configuration.setup.alert')}
      </Alert>

      <Card sx={{ py: 2, px: 4 }}>

        {
          isRetrievingSettings ? (
            <Grid container gap={2} sx={{ px: 2, mt: 2 }}>
              <Grid xs={12}>
                <Skeleton variant="rectangular" height={20} width="50%" />
              </Grid>
              <Grid xs={12}>
                <Skeleton variant="rectangular" height={200} />
              </Grid>
            </Grid>
          ) :
            (
              <FormProvider methods={methods}>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={12}>
                      <Alert variant="outlined" severity="info">
                        <Stack>
                          <Typography variant='body2'>
                            {t('integrations.detail.configuration.settings.webhook_url.alert')}
                          </Typography>
                          <Typography sx={{ mt: 1, fontWeight: 'bold' }} variant='body2'>
                            {idibuWebhookUrl}
                          </Typography>
                        </Stack>
                      </Alert>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <RHFTextField
                        name="webhook_secret"
                        label={t('integrations.detail.configuration.settings.webhook_secret.label')}
                        placeholder={t('integrations.detail.configuration.settings.webhook_secret.placeholder')}
                        helperText={t('integrations.detail.configuration.settings.webhook_secret.helper_text')}
                      />
                    </Grid>
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <LoadingButton
                      type="submit"
                      onClick={handleSetupIntegration}
                      variant="contained"
                      disabled={!isValid}
                      loading={isSettingUpIntegration}
                    >
                      {t('integrations.detail.configuration.setup.complete_setup')}
                    </LoadingButton>
                  </Box>
                </Stack>
              </FormProvider>
            )
        }
      </Card>
    </Stack>
  );
}